import React, { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { normalizeChecklistItems } from '../../lib/checklistTemplates'
import type { ServiceItem } from '../../utils/serviceItems'
import { MobileSheet, mobile } from '../mobile'

type Category = { id: string; name: string }

type Props = {
  open: boolean
  onClose: () => void
  onCreated: (item: ServiceItem) => void
}

const emptyForm = () => ({
  service_category_id: '',
  name: '',
  description: '',
  work_description: '',
  default_checklist: [] as Array<{
    label: string
    sort_order: number
    photo_required?: boolean
  }>,
  is_package: false,
  bundle_child_ids: [] as string[],
  unit_type: '',
  base_price: '',
  is_active: true,
  is_seasonal: false,
  season_start_month: '',
  season_end_month: '',
})

/**
 * Stand-alone clone of the Service Catalog "Add Service Item" form (create-only).
 * Kept independent so the catalog page form can grow without changing this modal.
 */
export function CreateServiceItemModal({ open, onClose, onCreated }: Props) {
  const [form, setForm] = useState(emptyForm())
  const [categories, setCategories] = useState<Category[]>([])
  const [catalogItems, setCatalogItems] = useState<Array<{ id: string; name: string; is_package?: boolean }>>([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setForm(emptyForm())
    const load = async () => {
      const [{ data: cats }, { data: items }] = await Promise.all([
        supabase.from('service_categories').select('id, name').order('name'),
        supabase
          .from('service_items')
          .select('id, name, is_package')
          .eq('is_active', true)
          .order('name'),
      ])
      setCategories(cats || [])
      setCatalogItems(items || [])
    }
    void load()
  }, [open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.service_category_id) {
      alert('Please select a category')
      return
    }
    if (!form.name.trim()) {
      alert('Please enter a name')
      return
    }

    try {
      setSaving(true)
      const itemData: Record<string, unknown> = {
        service_category_id: form.service_category_id,
        name: form.name.trim(),
        description: form.description || null,
        work_description: form.work_description || null,
        default_checklist: normalizeChecklistItems(form.default_checklist),
        is_package: form.is_package || false,
        unit_type: form.unit_type || 'flat_rate',
        base_price: parseFloat(form.base_price) || 0,
        is_active: form.is_active,
        is_seasonal: form.is_seasonal || false,
        season_start_month:
          form.is_seasonal && form.season_start_month
            ? parseInt(form.season_start_month, 10) || null
            : null,
        season_end_month:
          form.is_seasonal && form.season_end_month
            ? parseInt(form.season_end_month, 10) || null
            : null,
      }

      const { data: saved, error } = await supabase
        .from('service_items')
        .insert([itemData])
        .select(
          `
          id, service_category_id, name, description, work_description, default_checklist,
          unit_type, base_price, is_active, is_seasonal, season_start_month, season_end_month,
          service_categories!service_category_id(id, name)
        `
        )
        .single()

      if (error) throw error
      if (!saved?.id) throw new Error('No id returned')

      if (form.is_package && form.bundle_child_ids.length > 0) {
        const childRows = form.bundle_child_ids
          .filter((cid) => cid !== saved.id)
          .map((child_item_id, sort_order) => ({
            package_item_id: saved.id,
            child_item_id,
            sort_order,
          }))
        if (childRows.length > 0) {
          await supabase.from('service_item_bundle_children').insert(childRows)
        }
      }

      const cat = Array.isArray(saved.service_categories)
        ? saved.service_categories[0]
        : saved.service_categories

      const item: ServiceItem = {
        id: saved.id,
        service_category_id: saved.service_category_id || '',
        name: saved.name,
        description: saved.description,
        work_description: saved.work_description ?? null,
        default_checklist: Array.isArray(saved.default_checklist) ? saved.default_checklist : [],
        unit_type: saved.unit_type || 'flat_rate',
        base_price: parseFloat(saved.base_price) || 0,
        is_active: saved.is_active !== false,
        is_seasonal: !!saved.is_seasonal,
        season_start_month: saved.season_start_month ?? null,
        season_end_month: saved.season_end_month ?? null,
        category: cat ? { id: cat.id, name: cat.name } : undefined,
      }

      onCreated(item)
      onClose()
    } catch (err: any) {
      console.error('Error creating service item:', err)
      alert(err?.message || 'Failed to create service item')
    } finally {
      setSaving(false)
    }
  }

  return (
    <MobileSheet
      open={open}
      onClose={onClose}
      title="Add Service Item"
      maxWidthClass="max-w-md"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={`${mobile.secondaryBtn} flex-1`}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="create-service-item-form"
            disabled={saving}
            className={`${mobile.primaryBtn} flex-1`}
          >
            {saving ? 'Creating…' : 'Create'}
          </button>
        </>
      }
    >
          <form id="create-service-item-form" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className={mobile.label}>Category *</label>
              <select
                value={form.service_category_id}
                onChange={(e) => setForm({ ...form, service_category_id: e.target.value })}
                className={mobile.field}
                required
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={mobile.label}>Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={mobile.field}
                required
              />
            </div>
            <div>
              <label className={mobile.label}>Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                className={mobile.field}
              />
            </div>
            <div>
              <label className={mobile.label}>Work to be done</label>
              <textarea
                value={form.work_description}
                onChange={(e) => setForm({ ...form, work_description: e.target.value })}
                rows={3}
                placeholder="Seeded into jobs when this item is on a visit"
                className={mobile.field}
              />
            </div>
            <div className="space-y-2">
              <label className={mobile.label}>Default checklist</label>
              <p className="text-xs text-gray-500">
                Each task is copied onto jobs (prefixed with this item&apos;s name).
              </p>
              {form.default_checklist.map((row, idx) => (
                <div key={idx} className="flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    value={row.label}
                    onChange={(e) => {
                      const next = [...form.default_checklist]
                      next[idx] = { ...next[idx], label: e.target.value }
                      setForm({ ...form, default_checklist: next })
                    }}
                    className={`${mobile.field} flex-1 min-w-[10rem] !min-h-[44px] text-sm`}
                    placeholder="Checklist task…"
                  />
                  <label className="inline-flex items-center gap-1.5 min-h-[44px] text-xs text-gray-600 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={!!row.photo_required}
                      onChange={(e) => {
                        const next = [...form.default_checklist]
                        next[idx] = { ...next[idx], photo_required: e.target.checked }
                        setForm({ ...form, default_checklist: next })
                      }}
                      className="rounded border-gray-300 h-4 w-4"
                    />
                    Photo required
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const next = form.default_checklist.filter((_, i) => i !== idx)
                      setForm({
                        ...form,
                        default_checklist: normalizeChecklistItems(next),
                      })
                    }}
                    className="min-h-[44px] px-2 text-red-500 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setForm({
                    ...form,
                    default_checklist: [
                      ...form.default_checklist,
                      {
                        label: '',
                        sort_order: form.default_checklist.length,
                        photo_required: false,
                      },
                    ],
                  })
                }
                className="text-sm text-blue-600 hover:underline"
              >
                + Add task
              </button>
            </div>
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="quick-item-package"
                  checked={form.is_package}
                  onChange={(e) => setForm({ ...form, is_package: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="quick-item-package" className="ml-2 block text-sm text-gray-900">
                  Package (one line item, child items for ops)
                </label>
              </div>
              {form.is_package && (
                <div className="max-h-40 overflow-y-auto space-y-1 border border-gray-200 rounded-md bg-white p-2">
                  {catalogItems
                    .filter((si) => !si.is_package)
                    .map((si) => {
                      const checked = form.bundle_child_ids.includes(si.id)
                      return (
                        <label
                          key={si.id}
                          className="flex items-center gap-2 text-sm text-gray-800"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              const next = checked
                                ? form.bundle_child_ids.filter((id) => id !== si.id)
                                : [...form.bundle_child_ids, si.id]
                              setForm({ ...form, bundle_child_ids: next })
                            }}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                          />
                          {si.name}
                        </label>
                      )
                    })}
                </div>
              )}
            </div>
            <div>
              <label className={mobile.label}>Unit Type</label>
              <select
                value={form.unit_type}
                onChange={(e) => setForm({ ...form, unit_type: e.target.value })}
                className={mobile.field}
              >
                <option value="">Flat rate (default)</option>
                <option value="flat_rate">Flat Rate</option>
                <option value="monthly">Monthly</option>
                <option value="per_cleaning">Per Cleaning</option>
                <option value="per_unit">Per Unit</option>
                <option value="per_project">Per Project</option>
                <option value="hourly">Hourly</option>
              </select>
            </div>
            <div>
              <label className={mobile.label}>Base Price</label>
              <input
                type="number"
                step="0.01"
                value={form.base_price}
                onChange={(e) => setForm({ ...form, base_price: e.target.value })}
                className={mobile.field}
                placeholder="0.00"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="quick-item-active"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="quick-item-active" className="ml-2 block text-sm text-gray-900">
                Active
              </label>
            </div>
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="quick-item-seasonal"
                  checked={form.is_seasonal}
                  onChange={(e) => setForm({ ...form, is_seasonal: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="quick-item-seasonal" className="ml-2 block text-sm text-gray-900">
                  Seasonal service
                </label>
              </div>
              {form.is_seasonal && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Season start month
                    </label>
                    <select
                      value={form.season_start_month}
                      onChange={(e) => setForm({ ...form, season_start_month: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                    >
                      <option value="">--</option>
                      {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(
                        (label, idx) => (
                          <option key={idx + 1} value={String(idx + 1)}>
                            {label}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Season end month
                    </label>
                    <select
                      value={form.season_end_month}
                      onChange={(e) => setForm({ ...form, season_end_month: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                    >
                      <option value="">--</option>
                      {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(
                        (label, idx) => (
                          <option key={idx + 1} value={String(idx + 1)}>
                            {label}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                </div>
              )}
            </div>
          </form>
    </MobileSheet>
  )
}
